import React, { useEffect, useMemo, useState } from "react";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./Logs.css";

const PAGE_SIZE = 20;

const ACTIONS = [
  "LOGIN",
  "LOGOUT",
  "PAGE_VIEW",
  "CREATE_USER",
  "UPDATE_USER",
  "CREATE_LEDGER_ENTRY",
  "UPDATE_LEDGER_ENTRY",
  "WITHDRAW_REQUEST",
  "OMIE_SYNC",
  "OMIE_ORDER_UPDATE",
  "OMIE_ORDER_CONFIRM",
  "OMIE_SYNC_ERROR",
];

const getArrayFromResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.logs)) return payload.logs;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (payload.data && Array.isArray(payload.data.logs)) return payload.data.logs;
  return [];
};

const normalizeLog = (item, index) => {
  const actorObj = item?.actor || item?.user || item?.usuario || item?.owner;
  const actorName =
    (typeof actorObj === "string" ? actorObj : null) ||
    actorObj?.name ||
    actorObj?.full_name ||
    actorObj?.username ||
    actorObj?.email ||
    item?.actor_name ||
    item?.user_name ||
    item?.username ||
    item?.email ||
    "Sistema";

  const roleValue =
    actorObj?.role ||
    actorObj?.access_type ||
    item?.role ||
    item?.access_type ||
    item?.user_role ||
    item?.actor_role;

  return {
    id: item?.id || item?._id || item?.uuid || `log-${index}`,
    timestamp:
      item?.timestamp ||
      item?.created_at ||
      item?.createdAt ||
      item?.date ||
      item?.time ||
      item?.at ||
      null,
    actor: actorName,
    role: roleValue,
    action:
      item?.action ||
      item?.event ||
      item?.type ||
      item?.operation ||
      item?.kind ||
      "Acesso",
    description:
      item?.description ||
      item?.message ||
      item?.details ||
      item?.detail ||
      item?.extra ||
      "",
    ip: item?.ip || item?.ip_address || item?.remote_ip || item?.address || "",
    origin:
      item?.origin ||
      item?.source ||
      item?.module ||
      item?.area ||
      item?.service ||
      "",
    path: item?.path || item?.url || item?.route || item?.endpoint || "",
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
};

const getRoleTag = (roleValue, actorName) => {
  const raw = String(roleValue || "").toUpperCase();
  if (raw.includes("ADMIN")) return "ADMIN";
  if (raw.includes("LICENSED") || raw.includes("USER")) return "USUARIO";
  if (actorName && actorName !== "Sistema") return "USUARIO";
  return "SISTEMA";
};

const Logs = () => {
  const [menuClose, setMenuClose] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);

  const [filtroRole, setFiltroRole] = useState("ALL");
  const [filtroAction, setFiltroAction] = useState("ALL");
  const [busca, setBusca] = useState("");

  const toggleMenu = () => setMenuClose((prev) => !prev);

  const carregarLogs = async (pageToLoad = 1, searchValue = "") => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      params.set("page", String(pageToLoad));
      params.set("limit", String(PAGE_SIZE));

      if (filtroRole !== "ALL") params.set("role", filtroRole);
      if (filtroAction !== "ALL") params.set("action", filtroAction);
      if (searchValue) params.set("search", searchValue);

      const res = await Api.get(`/logs?${params.toString()}`);
      const data = getArrayFromResponse(res.data);
      const normalizados = data.map(normalizeLog);
      setLogs(normalizados);

      const pag = res.data?.pagination || {};
      setTotal(Number(pag.total || 0));
      setTotalPaginas(Number(pag.totalPages || 1));
      setPaginaAtual(Number(pag.page || pageToLoad));
    } catch (err) {
      console.error("Erro ao carregar logs:", err);
      setLogs([]);
      setError("Nao foi possivel carregar os logs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const term = busca.trim();
    const timer = setTimeout(() => {
      carregarLogs(paginaAtual, term);
    }, 350);

    return () => clearTimeout(timer);
  }, [paginaAtual, filtroRole, filtroAction, busca]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroRole, filtroAction, busca]);

  const logsPaginados = useMemo(() => logs, [logs]);

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} logsActive="active" />

      <main className="main-content">
        <Topbar page="Logs de atividade" onToggleMenu={toggleMenu} />

        <section className="logs-card">
          <div className="logs-toolbar">
            <div className="logs-info">
              {loading ? "Carregando logs..." : `${total} logs encontrados`}
            </div>

            <div className="logs-filters">
              <select
                className="logs-select"
                value={filtroRole}
                onChange={(e) => setFiltroRole(e.target.value)}
              >
                <option value="ALL">Todos</option>
                <option value="ADMIN">Admin</option>
                <option value="USUARIO">Usuarios</option>
                <option value="SISTEMA">Sistema</option>
              </select>

              <select
                className="logs-select"
                value={filtroAction}
                onChange={(e) => setFiltroAction(e.target.value)}
              >
                <option value="ALL">Todas as acoes</option>
                {ACTIONS.map((action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ))}
              </select>

              <input
                className="logs-input"
                type="search"
                placeholder="Buscar por usuario, acao, origem, IP..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />

              <button className="logs-btn" type="button" onClick={() => carregarLogs(1, busca.trim())}>
                Atualizar
              </button>
            </div>
          </div>

          {error ? <div className="logs-error">{error}</div> : null}

          <div className="logs-table">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Usuario</th>
                  <th>Perfil</th>
                  <th>Acao</th>
                  <th>Detalhes</th>
                  <th>Origem / URL</th>
                </tr>
              </thead>
              <tbody>
                {logsPaginados.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center" }}>
                      Nenhum log encontrado
                    </td>
                  </tr>
                ) : null}

                {logsPaginados.map((log, index) => {
                  const roleTag = getRoleTag(log.role, log.actor);
                  const badgeClass =
                    roleTag === "ADMIN"
                      ? "log-badge log-badge-admin"
                      : roleTag === "USUARIO"
                      ? "log-badge log-badge-user"
                      : "log-badge log-badge-system";

                  return (
                    <tr key={`${log.id}-${index}`}>
                      <td>{formatDateTime(log.timestamp)}</td>
                      <td>{log.actor || "-"}</td>
                      <td>
                        <span className={badgeClass}>{roleTag}</span>
                      </td>
                      <td>{log.action || "-"}</td>
                      <td className="logs-desc">{log.description || "-"}</td>
                      <td className="logs-origin">
                        {[log.origin, log.path, log.ip].filter(Boolean).join(" | ") || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {total > PAGE_SIZE && (
            <div className="logs-pagination">
              <div className="logs-pagination-info">
                Mostrando {Math.min((paginaAtual - 1) * PAGE_SIZE + 1, total)} a {" "}
                {Math.min(paginaAtual * PAGE_SIZE, total)} de {total}
              </div>
              <div className="logs-pagination-controls">
                <button
                  type="button"
                  onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                  disabled={paginaAtual === 1}
                >
                  Anterior
                </button>
                <span>
                  Pagina {paginaAtual} de {totalPaginas}
                </span>
                <button
                  type="button"
                  onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
                  disabled={paginaAtual === totalPaginas}
                >
                  Proxima
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Logs;
